/**
 * TelemetryBar.jsx - Performance Optimized
 *
 * IMPORTANTE: Este componente SÓ é renderizado quando DeviceRow está expandido.
 * Zero hooks pesados - recebe dados via props.
 *
 * Ordem dos ícones:
 * 1. Satélites (SatelliteAlt)
 * 2. Bateria % (Battery*)
 * 3. Tensão V (faCarBattery)
 * 4. Combustível (LocalGasStation)
 * 5. Ignição (Key rotacionado)
 * 6. Movimento (Navigation/Stop)
 * 7. GPS Inválido (NoGPS)
 * 8. Bloqueio (Lock/LockOpen)
 * 9. Âncora (Anchor)
 * 10. Alarme (NotificationsActive)
 */

import { memo } from 'react';
import { Box, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { formatAlarm } from '../../common/util/formatter';
import {
  SatelliteAlt,
  Navigation,
  Stop,
  Battery20,
  Battery30,
  Battery60,
  Battery80,
  BatteryFull,
  BatteryChargingFull,
  Key,
  Lock,
  LockOpen,
  Anchor,
  NotificationsActive,
  LocalGasStation,
  GpsOff,
} from '@mui/icons-material';

// Cores inline - evita dependência de tokens externos (performance)
const COLORS = {
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  neutral: '#6b7280',
};

// ============================================
// FUNÇÕES HELPER FORA DO COMPONENTE
// Não recriam a cada render
// ============================================

const getBatteryIcon = (level, charging) => {
  if (charging) return BatteryChargingFull;
  if (level <= 20) return Battery20;
  if (level <= 40) return Battery30;
  if (level <= 60) return Battery60;
  if (level <= 80) return Battery80;
  return BatteryFull;
};

const getBatteryColor = (level) => {
  if (level < 20) return COLORS.error;
  if (level < 50) return COLORS.warning;
  return COLORS.success;
};

const getSatColor = (sat) => {
  if (sat >= 8) return COLORS.success;
  if (sat >= 4) return COLORS.warning;
  return COLORS.error;
};

const getVoltageColor = (voltage) => {
  if (voltage >= 12.5) return COLORS.success;
  if (voltage >= 11.5) return COLORS.warning;
  return COLORS.error;
};

const getFuelColor = (fuel) => {
  if (fuel >= 30) return COLORS.success;
  if (fuel >= 10) return COLORS.warning;
  return COLORS.error;
};

// ============================================
// STYLES
// ============================================

const useStyles = makeStyles()((theme) => ({
  // TelemetryBar compacto - ~32px de altura
  bar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    padding: '6px 10px',
    background: theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.03)'
      : 'rgba(0,0,0,0.02)',
    borderRadius: 6,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'default',
    transition: 'transform 0.15s ease',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  icon: {
    fontSize: 16,
    width: 16,
    height: 16,
  },
  value: {
    fontSize: '0.75rem',
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
    fontWeight: 600,
    lineHeight: 1,
  },
  alarm: {
    animation: '$pulse 0.6s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
    '50%': { opacity: 0.6, transform: 'scale(1.1)' },
  },
}));

// ============================================
// COMPONENTE PRINCIPAL - MEMO
// ============================================

const TelemetryBar = memo(({ position, device }) => {
  const { classes, cx } = useStyles();
  const t = useTranslation();

  // Early return se não tem dados
  if (!position) return null;

  // Extração direta - sem hooks, sem useMemo
  const { attributes = {} } = position;
  const sat = attributes.sat;
  const battery = attributes.batteryLevel;
  const charging = attributes.charge;
  const voltage = attributes.power || attributes.voltage || attributes.battery;
  const ignition = attributes.ignition;
  const motion = attributes.motion;
  const blocked = attributes.blocked;
  const alarm = attributes.alarm;
  const fuel = attributes.fuel;
  const valid = position.valid;
  const hasAnchor = device?.attributes?.hasAnchor;

  // Verificar se tem algum dado para mostrar
  const hasData = sat !== undefined || battery !== undefined
    || voltage !== undefined || ignition !== undefined
    || fuel !== undefined || blocked !== undefined;

  if (!hasData) return null;

  return (
    <Box className={classes.bar}>
      {/* 1. Satélites */}
      {sat !== undefined && (
        <Tooltip title={`Satélites: ${sat}`} arrow placement="top">
          <Box className={classes.item}>
            <SatelliteAlt
              className={classes.icon}
              sx={{ color: getSatColor(sat) }}
            />
            <span className={classes.value} style={{ color: getSatColor(sat) }}>
              {sat}
            </span>
          </Box>
        </Tooltip>
      )}

      {/* 2. Bateria */}
      {battery !== undefined && (() => {
        const BattIcon = getBatteryIcon(battery, charging);
        const color = getBatteryColor(battery);
        return (
          <Tooltip title={`Bateria: ${Math.round(battery)}%${charging ? ' (Carregando)' : ''}`} arrow placement="top">
            <Box className={classes.item}>
              <BattIcon className={classes.icon} sx={{ color }} />
              <span className={classes.value} style={{ color }}>
                {Math.round(battery)}%
              </span>
            </Box>
          </Tooltip>
        );
      })()}

      {/* 3. Tensão */}
      {voltage !== undefined && voltage > 0 && (
        <Tooltip title={`Tensão: ${voltage.toFixed(1)}V`} arrow placement="top">
          <Box className={classes.item}>
            <svg
              viewBox="0 0 24 24"
              className={classes.icon}
              style={{ color: getVoltageColor(voltage), fill: 'currentColor' }}
            >
              <path d="M16,6l-3,5h2l-2,7l5-7h-3l1-5H16z M4,4h14v2H4v12h6v2H4c-1.1,0-2-0.9-2-2V6C2,4.9,2.9,4,4,4z M18,4h2 c1.1,0,2,0.9,2,2v12c0,1.1-0.9,2-2,2h-6v-2h6V6h-2V4z" />
            </svg>
            <span className={classes.value} style={{ color: getVoltageColor(voltage) }}>
              {voltage.toFixed(1)}V
            </span>
          </Box>
        </Tooltip>
      )}

      {/* 4. Combustível */}
      {fuel !== undefined && (
        <Tooltip title={`Combustível: ${Math.round(fuel)}%`} arrow placement="top">
          <Box className={classes.item}>
            <LocalGasStation
              className={classes.icon}
              sx={{ color: getFuelColor(fuel) }}
            />
            <span className={classes.value} style={{ color: getFuelColor(fuel) }}>
              {Math.round(fuel)}%
            </span>
          </Box>
        </Tooltip>
      )}

      {/* 5. Ignição - SÓ ÍCONE (cor indica estado) */}
      {ignition !== undefined && (
        <Tooltip title={`Ignição: ${ignition ? 'Ligada' : 'Desligada'}`} arrow placement="top">
          <Box className={classes.item}>
            <Key
              className={classes.icon}
              sx={{
                color: ignition ? COLORS.success : COLORS.neutral,
                transform: 'rotate(45deg)',
              }}
            />
          </Box>
        </Tooltip>
      )}

      {/* 6. Movimento - SÓ ÍCONE (cor indica estado) */}
      {motion !== undefined && (
        <Tooltip title={motion ? 'Em movimento' : 'Parado'} arrow placement="top">
          <Box className={classes.item}>
            {motion ? (
              <Navigation className={classes.icon} sx={{ color: COLORS.success }} />
            ) : (
              <Stop className={classes.icon} sx={{ color: COLORS.neutral }} />
            )}
          </Box>
        </Tooltip>
      )}

      {/* 7. GPS Inválido - SÓ ÍCONE */}
      {valid === false && (
        <Tooltip title="GPS Inválido" arrow placement="top">
          <Box className={classes.item}>
            <GpsOff
              className={classes.icon}
              sx={{ color: COLORS.error }}
            />
          </Box>
        </Tooltip>
      )}

      {/* 8. Bloqueio - SÓ ÍCONE (cor indica estado) */}
      {blocked !== undefined && (
        <Tooltip title={blocked ? 'Bloqueado' : 'Desbloqueado'} arrow placement="top">
          <Box className={classes.item}>
            {blocked ? (
              <Lock className={classes.icon} sx={{ color: COLORS.error }} />
            ) : (
              <LockOpen className={classes.icon} sx={{ color: COLORS.neutral }} />
            )}
          </Box>
        </Tooltip>
      )}

      {/* 9. Âncora */}
      {hasAnchor && (
        <Tooltip title="Âncora Ativa" arrow placement="top">
          <Box className={classes.item}>
            <Anchor
              className={classes.icon}
              sx={{ color: device?.attributes?.lockOnExit ? COLORS.error : COLORS.success }}
            />
          </Box>
        </Tooltip>
      )}

      {/* 10. Alarme */}
      {alarm && (
        <Tooltip title={`${t('positionAlarm')}: ${formatAlarm(alarm, t)}`} arrow placement="top">
          <Box className={classes.item}>
            <NotificationsActive
              className={cx(classes.icon, classes.alarm)}
              sx={{ color: COLORS.error }}
            />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
});

TelemetryBar.displayName = 'TelemetryBar';
export default TelemetryBar;